<template>
  <v-container fluid class="payment-list pa-0">
    <v-data-table :headers="headers"
                  :items="payments"
                  :footer-props="{itemsPerPageOptions: [10, 20, 50], showFirstLastPage: true}"
                  multi-sort
                  :search="search"
                  :custom-filter="customFilter"
                  fixed-header
                  class="payment-list--table"
                  v-bind="$attrs">
      <template #header.data-table-select>
        <v-simple-checkbox :value="selectedItemIds.length === payments.length"
                           @input="selectedAllToggled"
                           :ripple="false"></v-simple-checkbox>
      </template>
      <template #item.data-table-select="{item, select}">
        <v-simple-checkbox :value="selectedItemIds.indexOf(item.id) !== -1"
                           @input="itemSelected(item)"
                           :key="item.id"
                           :ripple="false"></v-simple-checkbox>
      </template>
      <template #item.create_at="{value}">
        {{ $parseDate(value) }}
      </template>
      <template #item.name="{item}">
        {{ `${item.account.surname} ${item.account.given_name}` }}
      </template>
      <template #item.enrollment.event.name="{value}">
        {{ $sanitizeHtml(value) }}
      </template>
      <template #item.meta="{item}">
        {{ getItemMeta(item) }}
      </template>
      <template #item.total="{item}">
        {{ getItemTotal(item) }}
      </template>
      <template #item.paid_at="{item, value}">
        <v-btn icon
               v-if="!value"
               @click="markPaid(item)"
               :key="item.id+'mark'">
          <v-icon>fas fa-cash-register</v-icon>
        </v-btn>

        <v-btn icon
               v-if="value"
               @click="unmarkPaid(item)"
               :key="item.id+'unmark'">
          <v-icon>fas fa-times-circle</v-icon>
        </v-btn>

        <v-btn icon
               v-if="hasParticipantPaid(item) && canParticipantBeRefunded(item)"
               @click="refundPayment(item)"
               :key="item.id+'refund'">
          <v-icon>fas fa-hand-holding-usd</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import {computed, onMounted, ref, watch} from '@vue/composition-api'
import _ from "lodash";

export default {
  name: 'PaymentList',
  props: {
    search: String,
    eventId: String,
    refreshTrigger: Boolean,
    feeTiers: String,
  },
  setup(props, {root, emit}) {
    const refresh = function () {
      getPayments();
    };
    onMounted(() => {
      refresh();
    });

    watch(() => props.refreshTrigger, (newValue) => {
      if (newValue) {
        refresh();
        emit('update:refresh-trigger', false);
      }
    })

    // Filter
    const filterOptions = computed(() => {
      return [
        {text: root.$t('admin_company.name_en'), value: 'name_en', contains: true},
        {text: root.$t('admin_company.name_zh'), value: 'name_zh', contains: true},
      ]
    })

    // Data Table
    const headers = ref([
      {text: root.$t('admin_participant.event'), value: 'enrollment.event.name'},
      {text: root.$t('admin_participant.session'), value: 'enrollment.session.name'},
      {
        text: root.$t('admin_participant.account'), value: 'name',
        // filter(value, search, item) {
        //   console.log(
        //       search !== null,
        //       item.account.surname.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1,
        //       item.account.given_name.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1,
        //       !search && item.account.surname.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1 && item.account.given_name.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
        //   );
        //
        //   return !search || item.account.surname.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1 || item.account.given_name.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
        // },
      },
      {text: root.$t('admin_participant.company'), value: 'account.company.name'},
      {text: root.$t('admin_enrollment.meta'), value: 'meta'},
      {text: root.$t('admin_payment.total'), value: 'total'},
      {text: root.$t('admin_participant.mark_paid'), value: 'paid_at', filterable: false},
    ]);
    const customFilter = function (value, search, item) {
      if (!search) return true
      search = search.toString().toLowerCase()
      if (search.trim() === '') return true


      let defaultFilter = function (value, search, item) {
        return value != null &&
            search != null &&
            typeof value !== 'boolean' &&
            value.toString().toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
      };

      return defaultFilter(value, search, item) ||
          Object.keys(item).some(key => {
            return defaultFilter(root.$resolve(item, key), search, item)
          }) ||
          item.account.surname.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1 ||
          item.account.given_name.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1;
    }

    const payments = ref([]);
    const getPayments = function () {
      root.$callAPI('hkiarace/admin/event/participant/', {
        event_id: props.eventId,
      }, function (response) {
        payments.value = response.body.participants;
      });
    };
    const getItemMeta = function (item) {
      if (props.feeTiers) {
        let meta = JSON.parse(item.enrollment.meta);
        let fee_tiers = JSON.parse(props.feeTiers);
        let components = [];

        fee_tiers.forEach((t) => {
          if (meta[t['id']]) {
            components.push(root.$localeGet(t, 'name') + ': ' + meta[t['id']]);
          }
        });

        return components.join('; ');
      }
    };
    const getItemTotal = function (item) {
      if (props.feeTiers) {
        let fee_tiers = JSON.parse(props.feeTiers);

        if (fee_tiers.length === 0) {
          // Free
          return 0;
        } else if (fee_tiers.length === 1 && !fee_tiers.name_en) {
          return fee_tiers[0].fee;
        } else {
          let meta = JSON.parse(item.enrollment.meta);

          let result = 0;

          fee_tiers.forEach(function (tier) {
            if (meta[tier.id]) {
              result += tier.fee * parseInt(meta[tier.id])
            }
          });

          return result;
        }
      }
    };

    const selectedAllToggled = function () {
      if (selectedItemIds.value.length !== payments.value.length) {
        selectedItemIds.value = payments.value.map((e) => e.id);
      } else {
        selectedItemIds.value = [];
      }
    }
    const itemSelected = function (item) {
      let idx = selectedItemIds.value.indexOf(item.id);

      if (idx === -1) {
        selectedItemIds.value = _.union(selectedItemIds.value, [item.id]);
      } else {
        let array = selectedItemIds.value;
        // _.remove(array, (i) => {
        //   return i === item.id;
        // });
        array.splice(idx, 1)
        selectedItemIds.value = array;
      }
    };
    const selectedItemIds = ref([]);
    watch(() => selectedItemIds.value, (newValue) => {
      emit('input', newValue);
    });

    const editItem = function (item) {
      emit('edit', item);
    };

    const markPaid = function (item) {
      if (confirm(root.$t('admin_participant.mark_paid_confirm_msg'))) {
        root.$callAPI('hkiarace/admin/event/participant/mark_paid/', {participant_id: item.id}, (response) => {
          root.$showAlertMessage(root.$t('admin_participant.mark_paid_success_msg'));
          refresh();
        });
      }
    }
    const unmarkPaid = function (item) {
      if (confirm(root.$t('admin_participant.unmark_paid_confirm_msg'))) {
        root.$callAPI('hkiarace/admin/event/participant/unmark_paid/', {participant_id: item.id}, (response) => {
          root.$showAlertMessage(root.$t('admin_participant.unmark_paid_success_msg'));
          refresh();
        });
      }
    }
    const notRefunded = function (paymentRecord) {
      let refunded = false;
      if (paymentRecord.payment_record.payment_method === 'cyber_source') {
        paymentRecord.payment_record.cyber_source_payment_records.forEach((r) => {
          if (r.decision === 'REFUND') {
            refunded = true;
          }
        })
      } else if (paymentRecord.payment_record.payment_method === 'union_pay') {
        paymentRecord.payment_record.union_pay_payment_records.forEach((r) => {
          let requestParams = JSON.parse(r.request_param);

          if (requestParams.txnType === '04') {
            refunded = true;
          }
        })
      }

      return !refunded;
    };
    const paymentSuccess = function (paymentRecord) {
      let paymentSuccess = false;
      if (paymentRecord.payment_record.payment_method === 'cyber_source') {
        paymentRecord.payment_record.cyber_source_payment_records.forEach((r) => {
          if (r.decision === 'ACCEPT') {
            paymentSuccess = true;
          }
        })
      } else if (paymentRecord.payment_record.payment_method === 'union_pay') {
        paymentRecord.payment_record.union_pay_payment_records.forEach((r) => {
          if (r.response_code === '00') {
            paymentSuccess = true;
          }
        })
      }

      return paymentSuccess;
    }
    const hasParticipantPaid = function (participant) {
      let paid = false;

      participant.hkiarace_event_payment_records.forEach((r) => {
        if (paymentSuccess(r)) {
          paid = true;
        }
      });

      return paid;
    }
    const canParticipantBeRefunded = function (item) {
      let canBeRefunded = false;

      item.hkiarace_event_payment_records.forEach((r) => {
        if (notRefunded(r)) {
          canBeRefunded = true;
        }
      });

      return canBeRefunded;
    }
    const refundPayment = function (item) {
      if (confirm(root.$t('admin_participant.refund_confirm_msg'))) {
        root.$callAPI('hkiarace/admin/event/participant/refund/', {participant_id: item.id}, (response) => {
          root.$showAlertMessage(root.$t('admin_participant.refund_success_msg'));
          refresh();
        });
      }
    }

    return {
      filterOptions,

      headers,
      customFilter,
      payments,

      getItemMeta,
      getItemTotal,

      selectedAllToggled,
      itemSelected,
      selectedItemIds,

      editItem,

      markPaid,
      unmarkPaid,
      hasParticipantPaid,
      canParticipantBeRefunded,
      refundPayment,
    }
  }
}
</script>

<style lang="less">
.payment-list {
  display: flex;
  flex-direction: column;
  height: 100%;

  .payment-list--table {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;

    .v-data-table__wrapper {
      flex: 1 1 0;
    }

    .v-data-footer {
      margin-right: 0 !important;
    }
  }
}
</style>
